import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { useToast } from 'hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useGetAgentIntegrationsOverview } from 'hooks/use-get-agent-integrations-overview';
import { useGetIntegrationConnectionsByChannels } from 'hooks/use-get-integration-connections';
import {
  useConnectTelegramIntegration,
  useConnectWhatsAppIntegration,
  useConnectTeamsIntegration,
  useActivateTelegramIntegration,
  useActivateWhatsAppIntegration,
  useActivateTeamsIntegration,
  useDeactivateTelegramIntegration,
  useDeactivateWhatsAppIntegration,
  useDeactivateTeamsIntegration,
} from 'hooks/use-create-integration-connection';
import {
  AgentIntegrationOverviewCard,
  AgentIntegrationOverviewCardSkeleton,
} from './agent-integration-overview-card';
import { TelegramBotConfigModal } from './telegram-bot-config-modal';
import { WhatsAppConfigModal } from './whatsapp-config-modal';
import { TeamsConfigModal } from './teams-config-modal';
import { TelegramConnectedModal } from './telegram-connected-modal';
import { TelegramBotsListModal } from './telegram-bots-list-modal';
import { TelegramDisconnectConfirmModal } from './telegram-disconnect-confirm-modal';
import {
  getOverviewChannelIds,
  isIntegrationConnected,
  mapOverviewItemToCard,
  shouldShowInAppIntegrationList,
} from './provider-utils';
import { getWhatsAppChatUrl, type WhatsAppConnectionConfig } from './whatsapp-config';
import { getTeamsChatUrl, type TeamsConnectionConfig } from './teams-config';
import type { ActiveIntegrationConnection } from 'services/integration-service';

const integrationsGridClassName =
  'grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 [&>*]:h-full';

type AgentIntegrationAppProps = {
  agentId: string;
  canEdit?: boolean;
};

export const AgentIntegrationApp = ({ agentId, canEdit: _canEdit = true }: AgentIntegrationAppProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isTelegramSetupModalOpen, setIsTelegramSetupModalOpen] = useState(false);
  const [isWhatsAppSetupModalOpen, setIsWhatsAppSetupModalOpen] = useState(false);
  const [isWhatsAppListModalOpen, setIsWhatsAppListModalOpen] = useState(false);
  const [isWhatsAppConnectedModalOpen, setIsWhatsAppConnectedModalOpen] = useState(false);
  const [whatsAppConnectionUrl, setWhatsAppConnectionUrl] = useState<string | null>(null);
  const [isTeamsSetupModalOpen, setIsTeamsSetupModalOpen] = useState(false);
  const [isTeamsListModalOpen, setIsTeamsListModalOpen] = useState(false);
  const [isTeamsConnectedModalOpen, setIsTeamsConnectedModalOpen] = useState(false);
  const [teamsConnectionUrl, setTeamsConnectionUrl] = useState<string | null>(null);
  const [isTelegramBotsListModalOpen, setIsTelegramBotsListModalOpen] = useState(false);
  const [isTelegramConnectedModalOpen, setIsTelegramConnectedModalOpen] = useState(false);
  const [isTelegramDisconnectConfirmOpen, setIsTelegramDisconnectConfirmOpen] = useState(false);
  const [telegramConnectionUrl, setTelegramConnectionUrl] = useState<string | null>(null);
  const [pendingDisconnectConnectionId, setPendingDisconnectConnectionId] = useState<string | null>(
    null
  );
  const [pendingDisconnectChannel, setPendingDisconnectChannel] = useState<
    'telegram' | 'whatsapp' | 'teams'
  >('telegram');
  const [busyTelegramConnectionId, setBusyTelegramConnectionId] = useState<string | null>(null);
  const [telegramSetupOpenedFromList, setTelegramSetupOpenedFromList] = useState(false);

  const {
    data: overview,
    isLoading,
    isError,
    refetch,
  } = useGetAgentIntegrationsOverview(agentId);

  const { mutateAsync: connectTelegramIntegration, isPending: isCreatingConnection } =
    useConnectTelegramIntegration();

  const { mutateAsync: connectWhatsAppIntegration, isPending: isCreatingWhatsAppConnection } =
    useConnectWhatsAppIntegration();

  const { mutateAsync: connectTeamsIntegration, isPending: isCreatingTeamsConnection } =
    useConnectTeamsIntegration();

  const { mutateAsync: activateTelegramIntegration, isPending: isActivatingConnection } =
    useActivateTelegramIntegration();

  const { mutateAsync: activateWhatsAppIntegration, isPending: isActivatingWhatsApp } =
    useActivateWhatsAppIntegration();

  const { mutateAsync: activateTeamsIntegration, isPending: isActivatingTeams } =
    useActivateTeamsIntegration();

  const { mutateAsync: deactivateTelegramIntegration, isPending: isDeactivatingConnection } =
    useDeactivateTelegramIntegration();

  const { mutateAsync: deactivateWhatsAppIntegration, isPending: isDeactivatingWhatsApp } =
    useDeactivateWhatsAppIntegration();

  const { mutateAsync: deactivateTeamsIntegration, isPending: isDeactivatingTeams } =
    useDeactivateTeamsIntegration();

  const invalidateTelegramQueries = async () => {
    await Promise.all([
      refetch(),
      queryClient.invalidateQueries({ queryKey: ['integrationConnections'] }),
    ]);
  };

  const channelIds = useMemo(
    () => getOverviewChannelIds(overview?.items ?? []),
    [overview?.items]
  );

  const { data: connectionsByChannel = {} } = useGetIntegrationConnectionsByChannels(
    channelIds,
    agentId,
    !!agentId
  );

  const telegramConnections = connectionsByChannel.telegram ?? [];
  const whatsappConnections = connectionsByChannel.whatsapp ?? [];
  const teamsConnections =
    connectionsByChannel.teams ?? connectionsByChannel['microsoft-teams'] ?? [];
  const hasTelegramBots = telegramConnections.length > 0;
  const hasWhatsAppConnections = whatsappConnections.length > 0;
  const hasTeamsConnections = teamsConnections.length > 0;

  const getChannelConnectionCount = (channelId: string) =>
    connectionsByChannel[channelId]?.length ?? 0;

  const getChannelConnectedCount = (channelId: string) =>
    (connectionsByChannel[channelId] ?? []).filter((connection) =>
      isIntegrationConnected(connection.status)
    ).length;

  const telegramConnectionId = useMemo(
    () =>
      overview?.items.find((item) => item.provider.provider_id === 'telegram')?.connection_id ??
      null,
    [overview?.items]
  );

  const integrations = useMemo(() => {
    const query = search.trim().toLowerCase();
    const items = (overview?.items ?? [])
      .filter(shouldShowInAppIntegrationList)
      .map((item) => mapOverviewItemToCard(item, t));

    if (!query) return items;

    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.providerFamily.toLowerCase().includes(query)
    );
  }, [overview?.items, search, t]);

  const showTelegramConnectedSuccess = (
    botLink?: string | null,
    fallbackUrl?: string | null
  ) => {
    toast({
      variant: 'success',
      title: t('SUCCESS', { defaultValue: 'Success' }),
      description: t('TELEGRAM_CONNECTED_SUCCESS', {
        defaultValue: 'Telegram bot connected successfully.',
      }),
    });

    setTelegramConnectionUrl(botLink || fallbackUrl || null);
    setIsTelegramConnectedModalOpen(true);
  };

  const handleTelegramConnectSubmit = async (botToken: string) => {
    try {
      const activation = await connectTelegramIntegration({
        agentId,
        botToken,
      });

      showTelegramConnectedSuccess(activation.bot_link);
      await invalidateTelegramQueries();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description:
          error instanceof Error
            ? error.message
            : t('TELEGRAM_CONNECT_FAILED', {
                defaultValue: 'Failed to connect Telegram bot. Please try again.',
              }),
      });
      throw error;
    }
  };

  const handleTelegramDisconnect = async () => {
    const isWhatsApp = pendingDisconnectChannel === 'whatsapp';
    const isTeams = pendingDisconnectChannel === 'teams';
    const connectionId =
      pendingDisconnectConnectionId || (isWhatsApp || isTeams ? null : telegramConnectionId);
    if (!connectionId) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description: isWhatsApp
          ? t('WHATSAPP_DISCONNECT_FAILED', {
              defaultValue: 'No WhatsApp connection found to disconnect.',
            })
          : isTeams
            ? t('TEAMS_DISCONNECT_FAILED', {
                defaultValue: 'No Teams connection found to disconnect.',
              })
            : t('TELEGRAM_DISCONNECT_FAILED', {
                defaultValue: 'No Telegram connection found to disconnect.',
              }),
      });
      return;
    }

    try {
      if (isWhatsApp) {
        await deactivateWhatsAppIntegration(connectionId);
      } else if (isTeams) {
        await deactivateTeamsIntegration(connectionId);
      } else {
        await deactivateTelegramIntegration(connectionId);
      }

      toast({
        variant: 'success',
        title: t('SUCCESS', { defaultValue: 'Success' }),
        description: isWhatsApp
          ? t('WHATSAPP_DISCONNECTED_SUCCESS', {
              defaultValue: 'WhatsApp disconnected successfully.',
            })
          : isTeams
            ? t('TEAMS_DISCONNECTED_SUCCESS', {
                defaultValue: 'Teams bot disconnected successfully.',
              })
            : t('TELEGRAM_DISCONNECTED_SUCCESS', {
                defaultValue: 'Telegram bot disconnected successfully.',
              }),
      });

      setIsTelegramConnectedModalOpen(false);
      setTelegramConnectionUrl(null);
      setIsWhatsAppConnectedModalOpen(false);
      setWhatsAppConnectionUrl(null);
      setIsTeamsConnectedModalOpen(false);
      setTeamsConnectionUrl(null);
      setIsTelegramDisconnectConfirmOpen(false);
      setPendingDisconnectConnectionId(null);
      await invalidateTelegramQueries();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description:
          error instanceof Error
            ? error.message
            : isWhatsApp
              ? t('WHATSAPP_DISCONNECT_FAILED', {
                  defaultValue: 'Failed to disconnect WhatsApp. Please try again.',
                })
              : isTeams
                ? t('TEAMS_DISCONNECT_FAILED', {
                    defaultValue: 'Failed to disconnect Teams bot. Please try again.',
                  })
                : t('TELEGRAM_DISCONNECT_FAILED', {
                    defaultValue: 'Failed to disconnect Telegram bot. Please try again.',
                  }),
      });
      throw error;
    }
  };

  const handleTelegramDisconnectRequest = (connectionId?: string | null) => {
    setPendingDisconnectChannel('telegram');
    setPendingDisconnectConnectionId(connectionId ?? telegramConnectionId);
    setIsTelegramDisconnectConfirmOpen(true);
  };

  const handleTelegramBotConnect = async (connection: ActiveIntegrationConnection) => {
    setBusyTelegramConnectionId(connection.id);

    try {
      const activation = await activateTelegramIntegration(connection.id);
      showTelegramConnectedSuccess(activation.bot_link, connection.connection_url);
      await invalidateTelegramQueries();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description:
          error instanceof Error
            ? error.message
            : t('TELEGRAM_CONNECT_FAILED', {
                defaultValue: 'Failed to connect Telegram bot. Please try again.',
              }),
      });
    } finally {
      setBusyTelegramConnectionId(null);
    }
  };

  const handleTelegramBotDisconnectRequest = (connection: ActiveIntegrationConnection) => {
    handleTelegramDisconnectRequest(connection.id);
  };

  const handleStartChatting = (connectionUrl: string | null) => {
    setTelegramConnectionUrl(connectionUrl);
    setIsTelegramConnectedModalOpen(true);
  };

  const handleWhatsAppStartChatting = (connection?: ActiveIntegrationConnection | null) => {
    setWhatsAppConnectionUrl(getWhatsAppChatUrl(connection));
    setIsWhatsAppListModalOpen(false);
    setIsWhatsAppConnectedModalOpen(true);
  };

  const handleWhatsAppReconnect = async (connection: ActiveIntegrationConnection) => {
    setBusyTelegramConnectionId(connection.id);

    try {
      await activateWhatsAppIntegration({
        connectionId: connection.id,
        agentId,
      });
      toast({
        variant: 'success',
        title: t('SUCCESS', { defaultValue: 'Success' }),
        description: t('WHATSAPP_CONNECTED_SUCCESS', {
          defaultValue: 'WhatsApp connected successfully.',
        }),
      });
      handleWhatsAppStartChatting(connection);
      await invalidateTelegramQueries();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description:
          error instanceof Error
            ? error.message
            : t('WHATSAPP_CONNECT_FAILED', {
                defaultValue: 'Failed to connect WhatsApp. Please try again.',
              }),
      });
    } finally {
      setBusyTelegramConnectionId(null);
    }
  };

  const handleTelegramShowList = () => {
    setIsTelegramBotsListModalOpen(true);
  };

  const handleTelegramOpenSetup = () => {
    setTelegramSetupOpenedFromList(false);
    setIsTelegramSetupModalOpen(true);
  };

  const handleTelegramBotStartChatting = (connection: ActiveIntegrationConnection) => {
    const connectionUrl =
      connection.connection_url ||
      connection.external_refs?.telegram?.bot_link ||
      null;

    setIsTelegramBotsListModalOpen(false);
    handleStartChatting(connectionUrl);
  };

  const handleTelegramAddBot = () => {
    setTelegramSetupOpenedFromList(true);
    setIsTelegramBotsListModalOpen(false);
    setIsTelegramSetupModalOpen(true);
  };

  const isTelegramBusy = isCreatingConnection || isActivatingConnection || isDeactivatingConnection;

  const handleWhatsAppOpenSetup = () => {
    setIsWhatsAppSetupModalOpen(true);
  };

  const handleTeamsStartChatting = (connection?: ActiveIntegrationConnection | null) => {
    setTeamsConnectionUrl(getTeamsChatUrl(connection));
    setIsTeamsListModalOpen(false);
    setIsTeamsConnectedModalOpen(true);
  };

  const handleTeamsOpenSetup = () => {
    setIsTeamsSetupModalOpen(true);
  };

  const handleTeamsReconnect = async (connection: ActiveIntegrationConnection) => {
    setBusyTelegramConnectionId(connection.id);

    try {
      await activateTeamsIntegration(connection.id);
      toast({
        variant: 'success',
        title: t('SUCCESS', { defaultValue: 'Success' }),
        description: t('TEAMS_CONNECTED_SUCCESS', {
          defaultValue: 'Teams bot connected successfully.',
        }),
      });
      handleTeamsStartChatting(connection);
      await invalidateTelegramQueries();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description:
          error instanceof Error
            ? error.message
            : t('TEAMS_CONNECT_FAILED', {
                defaultValue: 'Failed to connect Teams. Please try again.',
              }),
      });
    } finally {
      setBusyTelegramConnectionId(null);
    }
  };

  const toUtcIsoString = (val: string) => {
    if (!val) return '';
    if (val.endsWith('Z')) return val;
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      return d.toISOString();
    }
    return `${val}:00Z`;
  };

  const handleTeamsConnectSubmit = async (config: TeamsConnectionConfig) => {
    try {
      const result = await connectTeamsIntegration({
        agentId,
        botName: config.botName.trim(),
        azureBotResourceName: config.azureBotResourceName.trim(),
        azureTenantId: config.azureTenantId.trim(),
        microsoftAppId: config.microsoftAppId.trim(),
        clientSecret: config.clientSecret.trim(),
        clientSecretExpiresAt: toUtcIsoString(config.clientSecretExpiresAt.trim()),
      });

      toast({
        variant: 'success',
        title: t('SUCCESS', { defaultValue: 'Success' }),
        description: t('TEAMS_CONFIGURED_SUCCESS', {
          defaultValue: 'Teams configuration saved. Copy the webhook URL into Azure Bot.',
        }),
      });
      await invalidateTelegramQueries();

      const connectionId =
        (result as any)?.connection_id ?? (result as any)?.id ?? '';
      const webhookUrl =
        (result as any)?.webhook_url ||
        (result as any)?.webhookUrl ||
        (result as any)?.messaging_endpoint ||
        (result as any)?.endpoint_url ||
        (result as any)?.url ||
        (result as any)?.data?.webhook_url ||
        (result as any)?.data?.webhookUrl ||
        '';

      return {
        webhookUrl,
        connectionId,
        ...result,
      };
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description:
          error instanceof Error
            ? error.message
            : t('TEAMS_CONNECT_FAILED', {
                defaultValue: 'Failed to connect Teams. Please try again.',
              }),
      });
      throw error;
    }
  };

  const handleTeamsActivate = async (connectionId: string) => {
    try {
      const activation = await activateTeamsIntegration(connectionId);
      toast({
        variant: 'success',
        title: t('SUCCESS', { defaultValue: 'Success' }),
        description: t('TEAMS_CONNECTED_SUCCESS', {
          defaultValue: 'Teams bot connected successfully.',
        }),
      });
      await invalidateTelegramQueries();
      setIsTeamsSetupModalOpen(false);

      const botLink = activation?.bot_link || 'https://teams.microsoft.com/';
      setTeamsConnectionUrl(botLink);
      setIsTeamsConnectedModalOpen(true);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description:
          error instanceof Error
            ? error.message
            : t('TEAMS_CONNECT_FAILED', {
                defaultValue: 'Failed to connect Teams. Please try again.',
              }),
      });
      throw error;
    }
  };

  const handleWhatsAppConnectSubmit = async (config: WhatsAppConnectionConfig) => {
    try {
      const result = await connectWhatsAppIntegration({
        agentId,
        ...config,
      });
      toast({
        variant: 'success',
        title: t('SUCCESS', { defaultValue: 'Success' }),
        description: t('WHATSAPP_CONFIGURED_SUCCESS', {
          defaultValue: 'WhatsApp configuration saved. Copy the webhook URL into Meta.',
        }),
      });
      await invalidateTelegramQueries();

      const connectionId = result.connection_id;
      if (!connectionId || !result.webhook_url) {
        return null;
      }

      return {
        webhookUrl: result.webhook_url,
        connectionId,
      };
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description:
          error instanceof Error
            ? error.message
            : t('WHATSAPP_CONNECT_FAILED', {
                defaultValue: 'Failed to connect WhatsApp. Please try again.',
              }),
      });
      throw error;
    }
  };

  const handleWhatsAppBindAndActivate = async (connectionId: string) => {
    try {
      await activateWhatsAppIntegration({ connectionId, agentId });
      toast({
        variant: 'success',
        title: t('SUCCESS', { defaultValue: 'Success' }),
        description: t('WHATSAPP_CONNECTED_SUCCESS', {
          defaultValue: 'WhatsApp connected successfully.',
        }),
      });
      await invalidateTelegramQueries();
      setIsWhatsAppSetupModalOpen(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description:
          error instanceof Error
            ? error.message
            : t('WHATSAPP_CONNECT_FAILED', {
                defaultValue: 'Failed to connect WhatsApp. Please try again.',
              }),
      });
      throw error;
    }
  };

  const handleChannelListOrSetup = (channelId: string) => {
    if (channelId === 'telegram') {
      if (hasTelegramBots) {
        handleTelegramShowList();
        return;
      }

      handleTelegramOpenSetup();
      return;
    }

    if (channelId === 'whatsapp') {
      if (hasWhatsAppConnections) {
        setIsWhatsAppListModalOpen(true);
        return;
      }

      handleWhatsAppOpenSetup();
      return;
    }

    if (channelId === 'teams' || channelId === 'microsoft-teams') {
      if (hasTeamsConnections) {
        setIsTeamsListModalOpen(true);
        return;
      }

      handleTeamsOpenSetup();
    }
  };

  const isChannelBusy = (channelId: string) => {
    if (channelId === 'telegram') {
      return isTelegramBusy;
    }

    if (channelId === 'whatsapp') {
      return isCreatingWhatsAppConnection || isActivatingWhatsApp || isDeactivatingWhatsApp;
    }

    if (channelId === 'teams' || channelId === 'microsoft-teams') {
      return isCreatingTeamsConnection || isActivatingTeams || isDeactivatingTeams;
    }

    return false;
  };

  const renderIntegrationList = () => {
    if (isLoading) {
      return (
        <div className={integrationsGridClassName}>
          {Array.from({ length: 6 }).map((_, index) => (
            <AgentIntegrationOverviewCardSkeleton key={index} />
          ))}
        </div>
      );
    }

    if (isError) {
      return (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-[#e7e7e7] bg-white px-4 py-8 text-center dark:border-border dark:bg-card">
          <p className="text-sm text-[#888888] dark:text-muted-foreground">
            {t('FAILED_TO_LOAD_INTEGRATIONS', {
              defaultValue: 'Failed to load integrations. Please try again.',
            })}
          </p>
          <Button type="button" variant="outline" size="sm" onClick={() => refetch()}>
            {t('RETRY', { defaultValue: 'Retry' })}
          </Button>
        </div>
      );
    }

    if (integrations.length === 0) {
      return (
        <div className="rounded-lg border border-[#e7e7e7] bg-white px-4 py-8 text-center dark:border-border dark:bg-card">
          <p className="text-sm text-[#888888] dark:text-muted-foreground">
            {t('NO_INTEGRATIONS_FOUND', { defaultValue: 'No integrations found' })}
          </p>
        </div>
      );
    }

    return (
      <div className={integrationsGridClassName}>
        {integrations.map((item) => {
          const connectedCount = getChannelConnectedCount(item.id);

          return (
            <AgentIntegrationOverviewCard
              key={item.id}
              item={connectedCount > 0 ? { ...item, connected: true } : item}
              isBusy={isChannelBusy(item.id)}
              onStartChatting={
                item.supportsChat
                  ? () => {
                      if (item.id === 'whatsapp') {
                        handleWhatsAppStartChatting(whatsappConnections[0]);
                      } else if (item.id === 'teams' || item.id === 'microsoft-teams') {
                        handleTeamsStartChatting(teamsConnections[0]);
                      } else {
                        handleStartChatting(item.connectionUrl);
                      }
                    }
                  : undefined
              }
              onShowList={
                item.supportsList ? () => handleChannelListOrSetup(item.id) : undefined
              }
              listActionType={getChannelConnectionCount(item.id) > 0 ? 'list' : 'settings'}
              botCount={
                item.supportsList && connectedCount > 0 ? connectedCount : undefined
              }
              countKind={item.id === 'telegram' ? 'bot' : 'connection'}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex w-full min-w-0 flex-col gap-5">
      <p className="text-base font-normal leading-[1.6] text-[#4f4f4f] dark:text-muted-foreground">
        {t('APP_INTEGRATION_DESCRIPTION', {
          defaultValue: 'Connect your channels to start automating conversations',
        })}
      </p>

      <div className="flex h-12 w-full min-w-0 items-center gap-3 rounded-lg border border-[#e7e7e7] bg-white p-3 sm:max-w-[460px] dark:border-border dark:bg-card">
        <Search className="size-4 shrink-0 text-[#888888]" aria-hidden />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('SEARCH_INTEGRATIONS', {
            defaultValue: 'Search integrations...',
          })}
          className="h-full min-h-0 flex-1 border-0 bg-transparent p-0 text-sm font-normal shadow-none placeholder:text-[#888888] focus-visible:ring-0 dark:bg-transparent"
        />
      </div>

      {renderIntegrationList()}

      <TelegramBotsListModal
        open={isTelegramBotsListModalOpen}
        onOpenChange={setIsTelegramBotsListModalOpen}
        agentId={agentId}
        onStartChatting={handleTelegramBotStartChatting}
        onConnect={handleTelegramBotConnect}
        onDisconnect={handleTelegramBotDisconnectRequest}
        onAddBot={handleTelegramAddBot}
        busyConnectionId={busyTelegramConnectionId}
      />

      <TelegramBotsListModal
        open={isWhatsAppListModalOpen}
        onOpenChange={setIsWhatsAppListModalOpen}
        agentId={agentId}
        channelId="whatsapp"
        onStartChatting={handleWhatsAppStartChatting}
        onConnect={handleWhatsAppReconnect}
        busyConnectionId={busyTelegramConnectionId}
        onDisconnect={(connection) => {
          setPendingDisconnectChannel('whatsapp');
          setPendingDisconnectConnectionId(connection.id);
          setIsTelegramDisconnectConfirmOpen(true);
        }}
        onAddBot={() => {
          setIsWhatsAppListModalOpen(false);
          setIsWhatsAppSetupModalOpen(true);
        }}
      />

      <TelegramBotConfigModal
        open={isTelegramSetupModalOpen}
        onOpenChange={setIsTelegramSetupModalOpen}
        onConnect={handleTelegramConnectSubmit}
        onCancel={() => {
          if (telegramSetupOpenedFromList) {
            setIsTelegramBotsListModalOpen(true);
          }
        }}
        isConnecting={isCreatingConnection}
      />

      <WhatsAppConfigModal
        open={isWhatsAppSetupModalOpen}
        onOpenChange={setIsWhatsAppSetupModalOpen}
        onSave={handleWhatsAppConnectSubmit}
        onBindAndActivate={handleWhatsAppBindAndActivate}
        isSaving={isCreatingWhatsAppConnection}
        isActivating={isActivatingWhatsApp}
      />

      <TeamsConfigModal
        open={isTeamsSetupModalOpen}
        onOpenChange={setIsTeamsSetupModalOpen}
        onSave={handleTeamsConnectSubmit}
        onActivate={handleTeamsActivate}
        isSaving={isCreatingTeamsConnection}
        isActivating={isActivatingTeams}
      />

      <TelegramBotsListModal
        open={isTeamsListModalOpen}
        onOpenChange={setIsTeamsListModalOpen}
        agentId={agentId}
        channelId="teams"
        onStartChatting={handleTeamsStartChatting}
        onConnect={handleTeamsReconnect}
        busyConnectionId={busyTelegramConnectionId}
        onDisconnect={(connection) => {
          setPendingDisconnectChannel('teams');
          setPendingDisconnectConnectionId(connection.id);
          setIsTelegramDisconnectConfirmOpen(true);
        }}
        onAddBot={() => {
          setIsTeamsListModalOpen(false);
          setIsTeamsSetupModalOpen(true);
        }}
      />

      <TelegramConnectedModal
        open={isTelegramConnectedModalOpen}
        onOpenChange={setIsTelegramConnectedModalOpen}
        connectionUrl={telegramConnectionUrl}
      />

      <TelegramConnectedModal
        open={isWhatsAppConnectedModalOpen}
        onOpenChange={setIsWhatsAppConnectedModalOpen}
        connectionUrl={whatsAppConnectionUrl}
        title={t('WHATSAPP_CONNECTED', { defaultValue: 'WhatsApp Connected' })}
        logoSrc="/assets/images/agents/whatsapp-brand.svg"
        liveLabel={t('YOUR_WHATSAPP_NUMBER_IS_LIVE', {
          defaultValue: 'Your WhatsApp number is live',
        })}
        fallbackUrl="https://wa.me/"
      />

      <TelegramConnectedModal
        open={isTeamsConnectedModalOpen}
        onOpenChange={setIsTeamsConnectedModalOpen}
        connectionUrl={teamsConnectionUrl}
        title={t('TEAMS_CONNECTED', { defaultValue: 'Teams Connected' })}
        logoSrc="/assets/images/agents/teams.png"
        liveLabel={t('YOUR_TEAMS_BOT_IS_LIVE', {
          defaultValue: 'Your Microsoft Teams bot is live',
        })}
        fallbackUrl="https://teams.microsoft.com/"
      />

      <TelegramDisconnectConfirmModal
        open={isTelegramDisconnectConfirmOpen}
        onOpenChange={setIsTelegramDisconnectConfirmOpen}
        onConfirm={handleTelegramDisconnect}
        isLoading={isDeactivatingConnection || isDeactivatingWhatsApp || isDeactivatingTeams}
        description={
          pendingDisconnectChannel === 'whatsapp'
            ? t('WHATSAPP_DISCONNECT_CONFIRM_DESCRIPTION', {
                defaultValue:
                  'Your WhatsApp number will stop responding to chats until you reconnect it.',
              })
            : pendingDisconnectChannel === 'teams'
              ? t('TEAMS_DISCONNECT_CONFIRM_DESCRIPTION', {
                  defaultValue:
                    'Your Teams bot will stop responding to chats until you reconnect it.',
                })
              : undefined
        }
      />
    </div>
  );
};
